import { db } from '../firebase';
import { collection, getDocs, writeBatch, doc } from 'firebase/firestore';

// Define schema based on existing components
const VERSE_SCHEMA = {
  // Required fields (from UploadVerses and EditVerse)
  text: String,                    // verse text content
  reference: String,               // bible reference

  // Optional fields (matching exact field names from components)
  url: String,                     // bible.com URL
  categories: Array,               // from categories input
  tags: Array,                     // from tags input
  question: String,                // reflection question
  growthArea: String,             // from GROWTH_AREAS dropdown
  devTopic: String,               // from DEVELOPMENT_TOPICS dropdown
  concept: String,                // concept field
  difficultyLevel: Number,        // 1-5 scale
  relatedVerses: Array,          // comma-separated references
  contextualMeaning: String,      // contextual meaning textarea
  
  // Metadata fields
  themeMetadata: {
    emotional: Number,
    spiritual: Number,
    relational: Number,
    vocational: Number,
    physical: Number,
    missional: Number
  },
  lastUpdated: Date               // timestamp from both components
};

export const updateDatabaseSchema = async () => {
  const batch = writeBatch(db);
  const versesRef = collection(db, 'verses');
  const snapshot = await getDocs(versesRef);
  let updateCount = 0;
  let currentBatch = batch;

  console.log('Starting database schema update...');

  for (const verseDoc of snapshot.docs) {
    const verse = verseDoc.data();
    const updates = {};
    let needsUpdate = false;

    // Check each field in schema
    Object.entries(VERSE_SCHEMA).forEach(([field, type]) => {
      if (field === 'themeMetadata') {
        // Handle nested themeMetadata object
        if (!verse[field] || typeof verse[field] !== 'object') {
          needsUpdate = true;
          updates[field] = {
            emotional: 0,
            spiritual: 0,
            relational: 0,
            vocational: 0,
            physical: 0,
            missional: 0
          };
        } else {
          // Ensure all theme fields exist
          const requiredThemes = ['emotional', 'spiritual', 'relational', 'vocational', 'physical', 'missional'];
          requiredThemes.forEach(theme => {
            if (verse[field][theme] === undefined) {
              needsUpdate = true;
              if (!updates[field]) updates[field] = { ...verse[field] };
              updates[field][theme] = 0;
            }
          });
        }
      } else if (verse[field] === undefined) {
        // Handle all other fields
        needsUpdate = true;
        updates[field] = getDefaultValue(type);
      }
    });

    if (needsUpdate) {
      console.log(`Updating verse ${verseDoc.id} with missing fields:`, updates);
      currentBatch.update(doc(db, 'verses', verseDoc.id), updates);
      updateCount++;

      // Commit batch every 500 updates
      if (updateCount % 500 === 0) {
        await currentBatch.commit();
        currentBatch = writeBatch(db);
        console.log(`Committed batch of 500 updates. Total updates: ${updateCount}`);
      }
    }
  }

  // Commit any remaining updates
  if (updateCount % 500 !== 0) {
    await currentBatch.commit();
    console.log(`Committed final batch. Total updates: ${updateCount}`);
  }

  return updateCount;
};

const getDefaultValue = (type) => {
  switch (type) {
    case String:
      return '';
    case Number:
      return 0;
    case Array:
      return [];
    case Date:
      return new Date();
    default:
      return null;
  }
};

// Helper function to run the update
export const runDatabaseUpdate = async () => {
  try {
    console.log('Starting database schema update...');
    const updatedCount = await updateDatabaseSchema();
    console.log(`Successfully updated ${updatedCount} verse records`);
    return updatedCount;
  } catch (error) {
    console.error('Error updating database schema:', error);
    throw error;
  }
};
